// src/services/deployService.js

import useSelectionStore from "@store/useSelectionStore";
import useGameStore from "@store/useGameStore";
import useMapStore from "@store/useMapStore";
import { deployUnitApi } from "@api/gameApi";
import { ClickContext } from "@utils/enums";
import feedback from "@components/ui/general/feedback/feedback";
import calculateDeployable from "@helpers/game/calculateDeployable";

/**
 * Enter deployment mode for a specific unit
 * Calculates deployable tiles and updates map visuals
 */
export function enterDeployMode(unit) {
  const { game } = useGameStore.getState();
  const { setHighlightTiles, setInteractionMode, setOverlayMode } = useMapStore.getState();
  const { setClickContext } = useSelectionStore.getState();

  if (!unit || !game) {
    console.warn("[deployService] Cannot enter deploy mode: missing unit or game");
    return;
  }

  // console.log("[deployService] Entering deploy mode for unit:", unit.name);

  // Calculate deployable tiles
  const tiles = calculateDeployable(game, unit);

  // console.log("[deployService] Deployable tiles calculated:", tiles.length);

  // Update map visuals
  setHighlightTiles(tiles);
  setOverlayMode("deploy");
  setInteractionMode("deploy");

  // Update selection context
  setClickContext(ClickContext.DEPLOY_UNIT);
}

/**
 * Exit deployment mode and clear all visuals
 */
export function exitDeployMode() {
  const { clearVisuals, setInteractionMode } = useMapStore.getState();
  const { setClickContext } = useSelectionStore.getState();

  // console.log("[deployService] Exiting deploy mode");

  clearVisuals();
  setInteractionMode("select");
  setClickContext(ClickContext.SELECT);
}

/**
 * Deploy unit at target position
 * Validates position, calls API, and exits deploy mode on success
 */
export async function deployAt(x, y) {
  const { currentClick } = useSelectionStore.getState();
  const { game } = useGameStore.getState();
  const { highlightTiles } = useMapStore.getState().visual;

  // Get the deployable unit from currentClick
  const unit = currentClick?.data;

  if (!unit) {
    throw new Error('[deployService] No unit selected for deployment');
  }

  if (!game) {
    throw new Error('[deployService] No game data available');
  }

  // Validate tile is in highlight
  const canDeploy = highlightTiles.some((t) => t.x === x && t.y === y);
  if (!canDeploy) {
    feedback.error("Cannot deploy at this position");
    return;
  }

  try {
    // console.log(`[deployService] Deploying ${unit.name} at (${x}, ${y})`);
    await deployUnitApi(game.id, unit.id, x, y);
    feedback.success(`${unit.name} deployed!`);
    exitDeployMode();
  } catch (err) {
    console.error("[deployService] Error deploying unit:", err);
    feedback.error("Failed to deploy unit");
  }
}

// Export as object for consistency
const deployService = {
  enterDeployMode,
  exitDeployMode,
  deployAt
};

export default deployService;
